import axios from 'axios';

import { downloadFileFromResponse } from 'src/helpers/downloadFileFromResponse';
import type {
	NewRateFileInput,
	NewRateFileRequestBody,
	RateFileFetchResponse,
	RateFileReprocessResponse,
	RateFileResponse,
	RateFileStatusResponse,
} from 'src/interfaces/RateFile';

function createApiUri(path: string = '') {
	return '/api/gtdcm/v1/rate-files' + path;
}

export const fetchRateFilesUri = createApiUri();
export async function fetchRateFiles({
	page,
	perPage,
}: {
	page: number;
	perPage: number;
}) {
	const response = await axios.get<RateFileFetchResponse>(fetchRateFilesUri, {
		params: { page, perPage },
	});

	return response.data.data;
}

export const reprocessRateFilesUri = createApiUri('/reprocess');
export async function reprocessRateFiles(filesIds: number[]) {
	const response = await axios.post<RateFileReprocessResponse>(
		reprocessRateFilesUri,
		filesIds
	);

	return response.data.data;
}

export const downloadInputRateFilesUri = createApiUri('/download/input');
export async function downloadInputRateFiles(filesIds: number[]) {
	const response = await axios.post(downloadInputRateFilesUri, filesIds, {
		responseType: 'blob',
	});

	downloadFileFromResponse(
		[response.data],
		response.headers['content-disposition']
	);
}

export const downloadOutputRateFilesUri = createApiUri('/download/output');
export async function downloadOutputRateFiles(filesIds: number[]) {
	const response = await axios.post(downloadOutputRateFilesUri, filesIds, {
		responseType: 'blob',
	});

	downloadFileFromResponse(
		[response.data],
		response.headers['content-disposition']
	);
}

export const deleteRateFilesUri = createApiUri('/delete');
export async function deleteRateFiles(filesIds: number[]) {
	await axios.post(deleteRateFilesUri, filesIds);

	return filesIds;
}

export const createRateFileUri = createApiUri();
export async function createRateFile({
	inputDate,
	locations,
	name,
	outputFormat,
	rateFileGscList,
	settings,
}: NewRateFileInput) {
	let request: NewRateFileRequestBody = {
		name,
		inputDate,
		outputFormatId: outputFormat.id,
		rateFileGscList: rateFileGscList ?? [],
		settings,
	};

	if (locations.type === 'upload') {
		request = {
			...request,
			locationsIds: [],
			addressFileId: locations.addressFileId,
			locationTemplateId: locations.locationMapping.id,
			settings: {
				...settings,
				gtdLocations: false,
			},
		};
	} else if (locations.type === 'select') {
		request = {
			...request,
			locationsIds: locations.value.map((location) => location.tjId),
			settings: {
				...settings,
				gtdLocations: false,
			},
		};
	} else {
		const { settings } = request;
		request = {
			...request,
			locationsIds: [],
			addressFileId: '',
			settings: {
				...settings,
				gtdLocations: true,
			},
		};
	}

	const response = await axios.post<RateFileResponse>(
		createRateFileUri,
		request
	);

	return response.data.data;
}

export const fetchRateFileStatusUri = createApiUri('/status');
export async function fetchRateFileStatus(fileId: number) {
	const response = await axios.get<RateFileStatusResponse>(
		`${fetchRateFileStatusUri}/${fileId}`
	);

	return response.data.data;
}
